import type { VaultPayloadEnvelope } from '@signbento/crypto'
import {
  canonicalizeJcs,
  decryptVaultPayload,
  encryptVaultPayload,
  parseJsonStrict,
  sha256Hex,
} from '@signbento/crypto'
import { StorageError } from './errors.js'

/**
 * Record/blob encryption for the vault: every payload is sealed with the vault
 * DEK and AAD-bound to its identity and revision (PRD §8.5, §11.2). Tampered or
 * unreadable ciphertext surfaces as CORRUPT_REVISION; absent bytes surface as
 * MISSING_LOCAL_BYTES at the vault layer.
 */
export interface VaultCryptoBox {
  sealRecord(
    kind: string,
    id: string,
    revision: number,
    plaintext: Uint8Array,
  ): Promise<VaultPayloadEnvelope>
  openRecord(
    kind: string,
    id: string,
    revision: number,
    envelope: VaultPayloadEnvelope,
  ): Promise<Uint8Array>
  sealBlob(key: string, bytes: Uint8Array): Promise<Uint8Array>
  openBlob(key: string, stored: Uint8Array): Promise<Uint8Array>
}

export interface VaultCryptoBoxOptions {
  readonly dek: CryptoKey
  readonly key_id: string
  readonly vault_id: string
}

const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder()

function corrupt(message: string, cause: unknown): StorageError {
  const detail = cause instanceof Error ? cause.message : String(cause)
  return new StorageError('OP_STORAGE_CORRUPT_REVISION', `${message}: ${detail}`)
}

export function createVaultCryptoBox(options: VaultCryptoBoxOptions): VaultCryptoBox {
  const { dek, key_id, vault_id } = options

  return {
    async sealRecord(kind, id, revision, plaintext) {
      return encryptVaultPayload({
        key: dek,
        key_id,
        content: plaintext,
        context: { vault_id, object_id: `record:${kind}:${id}`, revision },
      })
    },

    async openRecord(kind, id, revision, envelope) {
      try {
        return await decryptVaultPayload({
          key: dek,
          envelope,
          context: { vault_id, object_id: `record:${kind}:${id}`, revision },
        })
      } catch (error) {
        throw corrupt(`record ${kind}:${id} could not be decrypted`, error)
      }
    },

    async sealBlob(key, bytes) {
      const envelope = await encryptVaultPayload({
        key: dek,
        key_id,
        content: bytes,
        context: { vault_id, object_id: `blob:${key}`, revision: 1 },
      })
      return textEncoder.encode(JSON.stringify(envelope))
    },

    async openBlob(key, stored) {
      try {
        const envelope = parseJsonStrict(
          textDecoder.decode(stored),
        ) as unknown as VaultPayloadEnvelope
        return await decryptVaultPayload({
          key: dek,
          envelope,
          context: { vault_id, object_id: `blob:${key}`, revision: 1 },
        })
      } catch (error) {
        throw corrupt(`blob ${key} could not be decrypted`, error)
      }
    },
  }
}

/** Deterministic JSON bytes for record payloads (strict RFC 8785 input). */
export function encodeRecordPayload(value: unknown): Uint8Array {
  return textEncoder.encode(canonicalizeJcs(value))
}

export function decodeRecordPayload<T>(bytes: Uint8Array): T {
  try {
    return parseJsonStrict(textDecoder.decode(bytes)) as T
  } catch (error) {
    throw corrupt('record payload is not valid canonical JSON', error)
  }
}

export async function blobSha256(bytes: Uint8Array): Promise<string> {
  return sha256Hex(bytes)
}
